import { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useProjectCommunes } from 'src/hooks/useProjectCommunes';
import { useProjectVillages } from 'src/hooks/useProjectVillages';
import { useTpe } from 'src/hooks/useTpe';
import { useUpdateTpe } from 'src/hooks/useUpdateTpe';
import { useHttpClient } from 'src/security/hooks/axiosProvider';
import Breadcrumb from '../../breadcrumb';
import TpeForm from '../components/TpeForm';

const EditTpePage = (props: any) => {
  const history = useHistory();
  const { tpe, refetchTpe } = useTpe();
  const { updateTpe } = useUpdateTpe();
  const httpClient = useHttpClient(true);

  const [selectedCommuneId, setSelectedCommuneId] = useState<number | null>(null);

  const projectId: number | null = tpe?.project?.id ?? null;
  const { communes } = useProjectCommunes(projectId);
  const { villages: localities } = useProjectVillages(projectId, selectedCommuneId);

  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [selectedScoreId, setSelectedScoreId] = useState<number | null>(null);
  const [scoreRate, setScoreRate] = useState<number | string>('');
  const [scoreYear, setScoreYear] = useState<number | string>('');

  const [alertInfo, setAlertInfo] = useState<{ show: boolean; message: string; color: string }>({
    show: false,
    message: '',
    color: 'success'
  });

  const [scoreModalAlert, setScoreModalAlert] = useState<{ show: boolean; message: string; color: string }>({
    show: false,
    message: '',
    color: 'danger'
  });

  const showNotification = (message: string, color: 'success' | 'danger' | 'info' | 'warning') => {
    setAlertInfo({ show: true, message, color });
    setTimeout(() => {
      setAlertInfo({ show: false, message: '', color: 'success' });
    }, 6000);
  };

  const showScoreModalAlert = (message: string, color: 'success' | 'danger' | 'info' | 'warning') => {
    setScoreModalAlert({ show: true, message, color });
  };

  if (!tpe) {
    return (
      <Container fluid={true} className="mt-5 text-center">
        <div className="spinner-border text-success" role="status">
          <span className="sr-only">{props.t('tpe.loading')}</span>
        </div>
      </Container>
    );
  }

  const handleSubmit = async (data: any) => {

    try {
      const isUnchanged =
        data.name === tpe.name &&
        data.type === tpe.type &&
        data.genre === tpe.genre &&
        data.anneeCreation === tpe.anneeCreation &&
        Number(data.nbrActif) === tpe.nbrActif &&
        Number(data.nbrMember) === tpe.nbrMember &&
        Number(data.nbrWomen) === tpe.nbrWomen &&
        data.commune?.id === tpe.commune?.id &&
        data.village?.id === tpe.village?.id;

      if (isUnchanged) {
        showNotification(props.t('tpe.no_changes_tpe'), "info");
        return;
      }

      const payload = {
        ...tpe,
        ...data,
        nbrActif: Number(data.nbrActif),
        nbrMember: Number(data.nbrMember),
        nbrWomen: Number(data.nbrWomen),
      };

      await updateTpe(tpe.id, payload);

      if (refetchTpe) {
        refetchTpe();
      }
      showNotification(props.t('tpe.messages.update_success'), "success");
    } catch (err) {
      showNotification(props.t('tpe.messages.update_error'), "danger");
    }
  };

  const openScoreModal = (score: any) => {
    setSelectedScoreId(score.id);
    setScoreRate(score.rate || 0);
    const year = score.rating_date ? new Date(score.rating_date).getFullYear() : '';
    setScoreYear(year);
    setScoreModalAlert({ show: false, message: '', color: 'danger' });
    setIsScoreModalOpen(true);
  };

  const closeScoreModal = () => {
    setIsScoreModalOpen(false);
    setScoreModalAlert({ show: false, message: '', color: 'danger' });
  };

  const handleUpdateScore = async () => {
    if (!selectedScoreId || !tpe?.agroecoloscores) return;

    try {
      const currentScore = tpe.agroecoloscores.find((s: any) => s.id === selectedScoreId);
      const isRateUnchanged = parseFloat(scoreRate as string) === currentScore?.rate;

      const currentYear = currentScore?.rating_date ? new Date(currentScore.rating_date).getFullYear() : '';
      const isYearUnchanged = Number(scoreYear) === currentYear;

      if (isRateUnchanged && isYearUnchanged) {
        showScoreModalAlert(props.t('tpe.no_changes_score'), "info");
        return;
      }

      const isDuplicateYear = tpe.agroecoloscores.some(
        (s: any) => s.id !== selectedScoreId && new Date(s.rating_date).getFullYear() === Number(scoreYear)
      );

      if (isDuplicateYear) {
        showScoreModalAlert(props.t('tpe.score_duplicate_year'), "danger");
        return;
      }

      await httpClient.current?.put(`/api/tpe/agroecoscore/${selectedScoreId}`, {
        rate: parseFloat(scoreRate as string),
        rating_date: `${scoreYear}-01-01T00:00:00.000Z`
      });

      closeScoreModal();

      if (refetchTpe) {
        refetchTpe();
      }
      showNotification(props.t('tpe.score_update_success'), "success");
    } catch (error) {
      showScoreModalAlert(props.t('tpe.score_update_error'), "danger");
    }
  };

  return (
    <Fragment>
      <Breadcrumb
        parent={props.t('tpe.list')}
        url="tpe/list"
        title={props.t('tpe.edit')}
      />

      <Container fluid={true}>

        <Alert isOpen={alertInfo.show} color={alertInfo.color} fade={true}>
          {alertInfo.message}
        </Alert>

        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <TpeForm
                  tpe={tpe}
                  onSubmit={handleSubmit}
                  communes={communes}
                  localities={localities}
                  onCommuneChange={setSelectedCommuneId}
                />

                <div className="mt-4 d-flex justify-content-end pt-3 border-top" style={{ gap: '10px' }}>
                  <Button color="secondary" onClick={() => history.goBack()}>
                    {props.t('tpe.cancel')}
                  </Button>
                  <Button color="success" type="submit" form="tpeForm">
                    {props.t('tpe.update')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>{props.t('tpe.agroecoloscore_title')}</h5>
              </CardHeader>
              <CardBody>
                {tpe.agroecoloscores && tpe.agroecoloscores.length > 0 ? (
                  tpe.agroecoloscores.map((score: any, index: number) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center p-3 border rounded mb-2 bg-light text-dark"
                    >
                      <div>
                        <strong>{props.t('tpe.score')} :</strong> {score.rate} / 100 <br />
                        <strong>{props.t('tpe.evaluation_date')} :</strong>{' '}
                        {score.rating_date ? new Date(score.rating_date).toLocaleDateString() : 'N/A'}
                      </div>
                      <Button color="primary" size="sm" onClick={() => openScoreModal(score)}>
                        {props.t('tpe.modify_score')}
                      </Button>
                    </div>
                  ))
                ) : (
                  <Alert color="info" className="text-center mb-0">
                    {props.t('tpe.no_scores')}
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={isScoreModalOpen} toggle={closeScoreModal} centered>
        <ModalHeader toggle={closeScoreModal}>
          {props.t('tpe.modal_edit_score_title')}
        </ModalHeader>
        <ModalBody>
          <Alert isOpen={scoreModalAlert.show} color={scoreModalAlert.color} fade={true} toggle={() => setScoreModalAlert({ ...scoreModalAlert, show: false })}>
            {scoreModalAlert.message}
          </Alert>
          <FormGroup>
            <Label for="scoreRate">{props.t('tpe.score_label')}</Label>
            <Input
              type="number"
              id="scoreRate"
              value={scoreRate}
              onChange={(e) => {
                setScoreRate(e.target.value);
                setScoreModalAlert({ show: false, message: '', color: 'danger' });
              }}
              min="0"
              max="100"
              step="0.1"
            />
          </FormGroup>
          <FormGroup>
            <Label for="scoreYear">{props.t('tpe.evaluation_year_label')}</Label>
            <Input
              type="number"
              id="scoreYear"
              value={scoreYear}
              onChange={(e) => {
                setScoreYear(e.target.value === '' ? '' : parseInt(e.target.value, 10));
                setScoreModalAlert({ show: false, message: '', color: 'danger' });
              }}
              min="2020"
              max="2030"
              step="1"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeScoreModal}>
            {props.t('tpe.cancel')}
          </Button>
          <Button color="success" onClick={handleUpdateScore}>
            {props.t('tpe.save')}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default translate(EditTpePage);